import React, { useState, useEffect } from 'react';
import MultiSelectDropdown from '../layout/MultiSelectDropdown1';
import PicklistApi from '../../api/PicklistApi';

/**
 * PicklistFieldSelector - Wrapper around MultiSelectDropdown for picklist fields
 * Handles loading picklist values and converting between value_key/value_label format
 */
const PicklistFieldSelector = ({
  field,
  value,
  onChange,
  disabled = false,
  isMultiSelect = false,
}) => {
  const [picklistValues, setPicklistValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const picklistApi = new PicklistApi();

  // Load picklist values when field changes
  useEffect(() => {
    if (field?.id && (field.data_type === 'picklist' || field.data_type === 'multipicklist')) {
      loadPicklistValues();
    } else {
      setPicklistValues([]);
    }
  }, [field?.id]);

  // Parse value prop to set selected keys
  useEffect(() => {
    if (value) {
      if (isMultiSelect) {
        // For multi-select, split by semicolon
        const keys = value.split(';').filter(v => v.trim());
        setSelectedKeys(keys);
      } else {
        // For single select
        setSelectedKeys([value]);
      }
    } else {
      setSelectedKeys([]);
    }
  }, [value, isMultiSelect]);

  const loadPicklistValues = async () => {
    if (!field?.id) return;

    setLoading(true);
    try {
      const response = await picklistApi.getActivePicklistValues(field.id);
      if (response.data.success) {
        const values = response.data.data || [];
        // Convert to format expected by MultiSelectDropdown: { key, value }
        const formattedValues = values.map(item => ({
          key: item.value_key,
          value: item.value_label,
        }));
        setPicklistValues(formattedValues);
      }
    } catch (error) {
      console.error('Error loading picklist values:', error);
      setPicklistValues([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (selectedKeysArray) => {
    let finalKeys = selectedKeysArray;

    // For single select, only keep one selected item at a time
    if (!isMultiSelect) {
      // If multiple items are selected, only keep the last one
      finalKeys = selectedKeysArray.length > 1
        ? [selectedKeysArray[selectedKeysArray.length - 1]]
        : selectedKeysArray;
    }

    setSelectedKeys(finalKeys);

    if (isMultiSelect) {
      // Join with semicolon for multi-select
      const newValue = finalKeys.join(';');
      onChange(newValue);
    } else {
      // Single value for single select
      const newValue = finalKeys.length > 0 ? finalKeys[0] : '';
      onChange(newValue);
    }
  };

  // Don't render if not a picklist field
  if (!field || (field.data_type !== 'picklist' && field.data_type !== 'multipicklist')) {
    return null;
  }

  return (
    <MultiSelectDropdown
      selectedValues={selectedKeys}
      picklistValues={picklistValues}
      onChange={handleChange}
      disabled={disabled || loading}
      placeholder={loading ? 'Loading...' : (isMultiSelect ? 'Select values...' : 'Select value...')}
      isRequired={false}
      forceOpen={false}
    />
  );
};

export default PicklistFieldSelector;
